import { useState, useMemo, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import EventCard from './components/EventCard';
import FilterBar from './components/FilterBar';
import TrendingSection from './components/TrendingSection';
import EventDetailModal from './components/EventDetailModal';
import type { TimeFilter } from './components/FilterBar';
import {
  adminEvents,
  eventCategoryLabels,
  type EventCategory,
  type RadioEvent,
} from '@/mocks/adminEvents';
import { radioStations } from '@/mocks/radios';
import { SwalAlert } from '@/hooks/useSweetAlert';

const SAVED_KEY = 'rfw_saved_events';

function loadIds(key: string): number[] {
  try {
    const raw = localStorage.getItem(key);
    return raw ? JSON.parse(raw) : [];
  } catch {
    return [];
  }
}

function saveIds(key: string, ids: number[]) {
  localStorage.setItem(key, JSON.stringify(ids));
}

type PageState = 'loading' | 'error' | 'ready';

function getWeekRange(): { start: string; end: string } {
  const now = new Date();
  const day = now.getDay();
  const diff = now.getDate() - day + (day === 0 ? -6 : 1);
  const monday = new Date(now.setDate(diff));
  monday.setHours(0, 0, 0, 0);
  const sunday = new Date(monday);
  sunday.setDate(monday.getDate() + 6);
  sunday.setHours(23, 59, 59, 999);
  return {
    start: monday.toISOString().split('T')[0],
    end: sunday.toISOString().split('T')[0],
  };
}

function getMonthRange(): { start: string; end: string } {
  const now = new Date();
  const start = new Date(now.getFullYear(), now.getMonth(), 1);
  const end = new Date(now.getFullYear(), now.getMonth() + 1, 0, 23, 59, 59, 999);
  return {
    start: start.toISOString().split('T')[0],
    end: end.toISOString().split('T')[0],
  };
}

export default function EventsPage() {
  const [pageState, setPageState] = useState<PageState>('loading');
  const [search, setSearch] = useState('');
  const [category, setCategory] = useState('');
  const [status, setStatus] = useState('');
  const [modality, setModality] = useState('');
  const [showPast, setShowPast] = useState(false);
  const [timeFilter, setTimeFilter] = useState<TimeFilter>('');
  const [selectedEvent, setSelectedEvent] = useState<RadioEvent | null>(null);
  const [savedIds, setSavedIds] = useState<number[]>(() => loadIds(SAVED_KEY));

  useEffect(() => {
    const t = setTimeout(() => setPageState('ready'), 600);
    return () => clearTimeout(t);
  }, []);

  const persistSaved = useCallback((ids: number[]) => {
    setSavedIds(ids);
    saveIds(SAVED_KEY, ids);
  }, []);

  const allCategories = useMemo(() => {
    const set = new Set(adminEvents.map((e) => e.category));
    return Array.from(set) as EventCategory[];
  }, []);

  const filtered = useMemo(() => {
    let result = [...adminEvents];

    // Time filter (quick filters)
    if (timeFilter === 'hoy') {
      const today = new Date().toISOString().split('T')[0];
      result = result.filter((e) => e.date === today);
    } else if (timeFilter === 'esta-semana') {
      const { start, end } = getWeekRange();
      result = result.filter((e) => e.date >= start && e.date <= end);
    } else if (timeFilter === 'este-mes') {
      const { start, end } = getMonthRange();
      result = result.filter((e) => e.date >= start && e.date <= end);
    } else if (timeFilter === 'proximos') {
      result = result.filter((e) => e.status === 'publicado');
    }

    // Hide past by default
    if (!showPast) {
      result = result.filter((e) => e.status !== 'finalizado' && e.status !== 'cancelado');
    }

    if (search.trim()) {
      const q = search.toLowerCase();
      result = result.filter((e) => {
        const station = radioStations.find((s) => s.id === e.stationId);
        const stationName = station?.name || '';
        return (
          e.title.toLowerCase().includes(q) ||
          e.description.toLowerCase().includes(q) ||
          e.location.toLowerCase().includes(q) ||
          stationName.toLowerCase().includes(q)
        );
      });
    }
    if (category) result = result.filter((e) => e.category === category);
    if (status) result = result.filter((e) => e.status === status);
    if (modality) result = result.filter((e) => e.modality === modality);

    // Sort: published first, then draft, then past
    result.sort((a, b) => {
      const order: Record<string, number> = { publicado: 0, borrador: 1, finalizado: 2, cancelado: 3 };
      const d = (order[a.status] || 0) - (order[b.status] || 0);
      if (d !== 0) return d;
      return new Date(a.date).getTime() - new Date(b.date).getTime();
    });

    return result;
  }, [search, category, status, modality, showPast, timeFilter]);

  const trending = useMemo(() => {
    return [...adminEvents]
      .sort((a, b) => b.shares - a.shares)
      .slice(0, 6);
  }, []);

  const upcoming = useMemo(() => {
    return [...adminEvents]
      .filter((e) => e.status === 'publicado')
      .sort((a, b) => new Date(a.date).getTime() - new Date(b.date).getTime())
      .slice(0, 6);
  }, []);

  const featured = useMemo(() => {
    return adminEvents.filter((e) => e.highlighted && e.status !== 'cancelado').slice(0, 6);
  }, []);

  // --- Actions ---
  const handleViewEvent = useCallback((event: RadioEvent) => {
    setSelectedEvent(event);
  }, []);

  const handleShare = useCallback((event: RadioEvent) => {
    const url = `${window.location.origin}/events?event=${event.id}`;
    if (navigator.share) {
      navigator.share({ title: event.title, text: event.description.slice(0, 100), url });
    } else {
      navigator.clipboard.writeText(url).then(() => {
        SwalAlert.toast('¡Link copiado al portapapeles!', 'success');
      }).catch(() => {
        SwalAlert.toast('No se pudo copiar el link', 'error');
      });
      return;
    }
    SwalAlert.toast('¡Compartiendo evento!', 'success');
  }, []);

  const handleSave = useCallback((event: RadioEvent) => {
    if (savedIds.includes(event.id)) {
      persistSaved(savedIds.filter((id) => id !== event.id));
      SwalAlert.toast('Evento quitado de guardados', 'info');
    } else {
      persistSaved([...savedIds, event.id]);
      SwalAlert.toast('¡Evento guardado!', 'success');
    }
  }, [savedIds, persistSaved]);

  const handleRemind = useCallback(async (event: RadioEvent) => {
    const result = await SwalAlert.confirm(
      '¿Querés que te recordemos?',
      `Te avisaremos cuando se acerque "${event.title}" el ${new Date(event.date).toLocaleDateString('es-MX', { day: 'numeric', month: 'long', year: 'numeric' })}.`
    );
    if (result.isConfirmed) {
      SwalAlert.toast('¡Te recordaremos! Activá las notificaciones.', 'success');
    }
  }, []);

  const handleRetry = useCallback(() => {
    setPageState('loading');
    setTimeout(() => setPageState('ready'), 600);
  }, []);

  // Unique stations count
  const uniqueStationIds = useMemo(() => new Set(adminEvents.map((e) => e.stationId)), []);

  // --- Loading skeleton ---
  if (pageState === 'loading') {
    return (
      <div className="min-h-screen bg-background-50">
        <section className="relative w-full h-[280px] md:h-[380px] bg-background-200 animate-pulse" />
        <main className="max-w-[1280px] mx-auto px-4 md:px-6 py-8 md:py-12 space-y-8">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {[1,2,3,4].map((i) => (
              <div key={i} className="bg-background-100 rounded-2xl h-40 animate-pulse" />
            ))}
          </div>
          <div className="space-y-3">
            <div className="h-5 w-40 bg-background-100 rounded animate-pulse" />
            <div className="h-10 w-full bg-background-100 rounded-xl animate-pulse" />
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
              {[1,2,3,4,5,6,7,8].map((i) => (
                <div key={i} className="bg-background-100 rounded-2xl h-[380px] animate-pulse" />
              ))}
            </div>
          </div>
        </main>
      </div>
    );
  }

  // --- Error state ---
  if (pageState === 'error') {
    return (
      <div className="min-h-screen bg-background-50 flex flex-col items-center justify-center px-4 py-20">
        <div className="w-20 h-20 rounded-3xl bg-red-100 flex items-center justify-center mb-5">
          <i className="ri-error-warning-line text-3xl text-red-500"></i>
        </div>
        <h2 className="text-xl font-extrabold text-foreground-950 mb-2">Error al cargar eventos</h2>
        <p className="text-sm text-foreground-500 mb-6 text-center max-w-md">
          No pudimos cargar el directorio de eventos. Verificá tu conexión e intentá de nuevo.
        </p>
        <button
          onClick={handleRetry}
          className="inline-flex items-center gap-2 px-6 py-3 bg-primary-500 text-background-50 dark:text-foreground-950 rounded-xl text-sm font-bold hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap"
        >
          <i className="ri-refresh-line"></i>
          Reintentar
        </button>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background-50">
      {/* HERO BANNER */}
      <section className="relative w-full h-[280px] md:h-[380px] overflow-hidden">
        <img
          src="https://readdy.ai/api/search-image?query=Massive%20outdoor%20music%20festival%20event%20scene%2C%20colorful%20concert%20stage%20with%20spectacular%20lighting%20rig%2C%20huge%20energetic%20crowd%20with%20raised%20hands%2C%20warm%20golden%20sunset%20atmosphere%2C%20concert%20event%20banner%2C%20wide%20angle%20dynamic%20event%20photography%20with%20dramatic%20sky%20and%20festival%20energy&width=1600&height=800&seq=events-hero-banner&orientation=landscape"
          alt="Eventos Globales Radiofrecuenciaweb"
          className="w-full h-full object-cover object-top"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-black/40 via-black/20 to-black/60"></div>
        <div className="absolute inset-0 flex flex-col items-center justify-center px-4 text-center">
          <div className="w-16 h-16 rounded-2xl bg-background-50/15 backdrop-blur-md flex items-center justify-center mb-4 border border-background-50/20">
            <i className="ri-calendar-event-line text-3xl text-background-50"></i>
          </div>
          <h1 className="text-3xl md:text-5xl font-extrabold text-background-50 leading-tight mb-3">
            Directorio Global de Eventos
          </h1>
          <p className="text-sm md:text-base text-background-50/80 max-w-xl leading-relaxed">
            Descubre los mejores eventos de todas nuestras radios. Música, deportes, cultura, comunidad y mucho más en un solo lugar.
          </p>
          <div className="flex items-center gap-3 mt-5">
            <Link
              to="/radios"
              className="inline-flex items-center gap-2 px-5 py-2.5 bg-background-50/10 backdrop-blur-md border border-background-50/20 text-background-50 rounded-full text-xs font-bold hover:bg-background-50/20 transition-all whitespace-nowrap"
            >
              <i className="ri-radio-line"></i>
              Explorar Radios
            </Link>
            <span className="text-background-50/60 text-xs">{adminEvents.length} eventos activos</span>
          </div>
        </div>
      </section>

      {/* MAIN CONTENT */}
      <main className="max-w-[1280px] mx-auto px-4 md:px-6 py-8 md:py-12 space-y-8">
        {/* TRENDING SECTIONS */}
        <div className="space-y-6">
          {/* Featured */}
          <TrendingSection
            title="Eventos Destacados"
            icon="ri-star-fill"
            events={featured}
            variant="accent"
            onViewEvent={handleViewEvent}
            onShare={handleShare}
            onSave={handleSave}
            savedIds={savedIds}
          />

          {/* Most shared */}
          <TrendingSection
            title="Más Compartidos"
            icon="ri-fire-fill"
            events={trending}
            variant="primary"
            onViewEvent={handleViewEvent}
            onShare={handleShare}
            onSave={handleSave}
            savedIds={savedIds}
          />

          {/* Upcoming */}
          <TrendingSection
            title="Próximos Eventos"
            icon="ri-calendar-2-fill"
            events={upcoming}
            variant="default"
            onViewEvent={handleViewEvent}
            onShare={handleShare}
            onSave={handleSave}
            savedIds={savedIds}
          />
        </div>

        {/* ALL EVENTS */}
        <section>
          <div className="flex items-center gap-3 mb-5">
            <div className="w-10 h-10 rounded-xl bg-secondary-100 flex items-center justify-center">
              <i className="ri-grid-line text-base text-secondary-600"></i>
            </div>
            <h2 className="text-xl font-extrabold text-foreground-950">Todos los Eventos</h2>
            <span className="text-xs font-medium text-foreground-400 bg-background-100 rounded-full px-2.5 py-0.5">{filtered.length}</span>
          </div>

          <FilterBar
            search={search}
            onSearchChange={setSearch}
            category={category}
            onCategoryChange={setCategory}
            status={status}
            onStatusChange={setStatus}
            modality={modality}
            onModalityChange={setModality}
            timeFilter={timeFilter}
            onTimeFilterChange={setTimeFilter}
            categories={allCategories}
          />

          {/* Past toggle */}
          <div className="flex items-center justify-between mt-4">
            <p className="text-[11px] text-foreground-400">
              {filtered.length === 0 ? 'No se encontraron eventos con esos filtros.' : `Mostrando ${filtered.length} evento${filtered.length !== 1 ? 's' : ''}`}
            </p>
            <button
              onClick={() => setShowPast(!showPast)}
              className="inline-flex items-center gap-1.5 text-[11px] font-semibold text-foreground-500 hover:text-foreground-700 cursor-pointer transition-colors whitespace-nowrap"
            >
              <i className={`text-xs ${showPast ? 'ri-eye-off-line' : 'ri-eye-line'}`}></i>
              {showPast ? 'Ocultar finalizados' : 'Mostrar finalizados'}
            </button>
          </div>

          {/* Events Grid */}
          {filtered.length > 0 ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4 mt-4">
              {filtered.map((event) => (
                <EventCard
                  key={event.id}
                  event={event}
                  onClick={handleViewEvent}
                  onShare={handleShare}
                  onSave={handleSave}
                  isSaved={savedIds.includes(event.id)}
                />
              ))}
            </div>
          ) : (
            <div className="flex flex-col items-center justify-center py-16 text-center">
              <div className="w-16 h-16 rounded-2xl bg-background-100 flex items-center justify-center mb-4">
                <i className="ri-calendar-2-line text-2xl text-foreground-300"></i>
              </div>
              <p className="text-sm font-semibold text-foreground-400">No se encontraron eventos</p>
              <p className="text-[12px] text-foreground-300 mt-1">Intentá ajustar los filtros o la búsqueda</p>
            </div>
          )}
        </section>

        {/* STATS BAR */}
        <section className="bg-background-100 rounded-3xl p-6 flex flex-wrap gap-6 items-center justify-center">
          {[
            { icon: 'ri-calendar-event-line', label: 'Eventos Totales', value: adminEvents.length.toString() },
            { icon: 'ri-radio-line', label: 'Radios Participantes', value: uniqueStationIds.size.toString() },
            { icon: 'ri-star-fill', label: 'Destacados', value: featured.length.toString() },
            { icon: 'ri-calendar-2-fill', label: 'Próximos', value: upcoming.length.toString() },
            { icon: 'ri-price-tag-3-line', label: 'Categorías', value: allCategories.length.toString() },
          ].map((stat, i) => (
            <div key={i} className="flex items-center gap-3 px-4">
              <div className="w-10 h-10 rounded-xl bg-background-200 flex items-center justify-center">
                <i className={`${stat.icon} text-base text-foreground-500`}></i>
              </div>
              <div>
                <p className="text-lg font-extrabold text-foreground-950 leading-none">{stat.value}</p>
                <p className="text-[10px] font-semibold text-foreground-400 uppercase tracking-wider">{stat.label}</p>
              </div>
            </div>
          ))}
        </section>
      </main>

      {/* EVENT DETAIL MODAL */}
      {selectedEvent && (
        <EventDetailModal
          event={selectedEvent}
          onClose={() => setSelectedEvent(null)}
          onShare={handleShare}
          onSave={handleSave}
          onRemind={handleRemind}
          isSaved={savedIds.includes(selectedEvent.id)}
        />
      )}
    </div>
  );
}